import * as yup from 'yup';
import { calculateSplitFromMinorUnits, CountryCurrencyMap, PortalUserPermission, } from '../..';
import { AppliedProductId } from '../../types/AppliedProductId';
import { PaymentAttribute } from '../../types/PaymentAttribute';
import { hppPaymentMethods } from '../../types/PaymentMethod';
const validHexRegex = /^#(?:[0-9a-fA-F]{3}){1,2}$/;
const APPLIED_PLATFORMS = ['csr24', 'epic', 'ezl', 'mob'];
yup.addMethod(yup.string, 'validUrl', function (errorMessage) {
    return this.test('test-valid-url', errorMessage, (value) => {
        if (!value) {
            return true;
        }
        let url;
        try {
            url = new URL(value);
        }
        catch (_) {
            return false;
        }
        return url.protocol === 'http:' || url.protocol === 'https:';
    });
});
yup.addMethod(yup.array, 'unique', function (message, mapper = (a) => a) {
    return this.test('unique', message, function (list) {
        return !list || (list === null || list === void 0 ? void 0 : list.length) === new Set(list === null || list === void 0 ? void 0 : list.map(mapper)).size;
    });
});
yup.addMethod(yup.string, 'validHex', function (errorMessage) {
    return this.test('test-valid-hex', errorMessage, (value) => {
        if (!value) {
            return true;
        }
        if (value.match(validHexRegex)) {
            return true;
        }
        else {
            return false;
        }
    });
});
export const makePaymentSessionSchemaWithPortalUserPermissions = (merchantFees, permissions) => {
    var _a;
    return paymentSessionSchema({
        merchantFees: merchantFees,
        canUpdateConvenienceFeePermission: (_a = permissions[PortalUserPermission.CanChangeConvenienceFee]) !== null && _a !== void 0 ? _a : false,
        isSinglePaymentFlow: true,
    });
};
export const PUB_SUB_PAYMENT_ATTRIBUTE_PREFIX = 'pubsub_';
export const PUB_SUB_REGISTRATION_ATTRIBUTE_PREFIX = 'pubsub_';
export const paymentSessionSchema = (config) => {
    return yup
        .object({
        merchantId: yup.string().max(36).optional(),
        appliedProductId: yup.string().lowercase().oneOf(Object.values(AppliedProductId)).optional(),
        agentId: yup.string().optional(),
        agentName: yup.string().optional(),
        customer: yup
            .object()
            .shape({
            id: yup.string().optional(),
            firstName: yup.string().optional(),
            lastName: yup.string().optional(),
            businessName: yup.string().optional(),
            name: yup.string().optional(),
            email: yup.string().email().optional(),
        })
            .optional(),
        currency: yup.string().length(3).optional(),
        creditFeePercent: yup.number().integer().optional().min(0),
        fees: yup
            .object()
            .shape({
            credit: yup.number().integer().optional().min(0),
            ach: yup.number().integer().optional().min(0),
            eft: yup.number().integer().optional().min(0),
            useVariableAch: yup.boolean().optional(),
            useVariableEft: yup.boolean().optional(),
        })
            .optional()
            .test({
            name: 'credit',
            message: 'Received fee amounts are more than allowed',
            test: (receivedFeeAmounts, context) => {
                var _a, _b, _c;
                const { merchantId, amount, currency: _currency, } = context.parent;
                const fees = config.merchantFees[merchantId];
                const currency = 
                // lint is now failing this line
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                _currency !== null && _currency !== void 0 ? _currency : CountryCurrencyMap[process.env.LOCALE.toUpperCase()];
                const [calculatedAppliedConvenienceFee] = calculateSplitFromMinorUnits(fees.appliedSplitBasisPoints, amount, currency);
                const [calculatedMerchantConvenienceFee] = calculateSplitFromMinorUnits(fees.hppCreditFeePercent, amount, currency);
                // add 35% to the limit, per Mike, to allow for $1 test transactions
                const errorMargin = 0.35;
                const calculatedConvenienceFeeWithMarginForError = calculatedAppliedConvenienceFee.amount * (1 + errorMargin);
                // no one is allowed to exceed applied limit
                if (((_a = receivedFeeAmounts.credit) !== null && _a !== void 0 ? _a : 0) > calculatedConvenienceFeeWithMarginForError)
                    return context.createError({
                        message: `Received credit fee of ${receivedFeeAmounts.credit} exceeds limit of ${calculatedAppliedConvenienceFee.amount}.`,
                    });
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                if (((_b = receivedFeeAmounts.ach) !== null && _b !== void 0 ? _b : 0) > fees.appliedAchFlatFeeAmount)
                    return context.createError({
                        message: `Received ach fee of ${receivedFeeAmounts.ach} exceeds limit of ${fees.appliedAchFlatFeeAmount}.`,
                    });
                if (((_c = receivedFeeAmounts.eft) !== null && _c !== void 0 ? _c : 0) > fees.appliedEftFlatFeeAmount)
                    return context.createError({
                        message: `Received eft fee of ${receivedFeeAmounts.eft} exceeds limit of ${fees.appliedEftFlatFeeAmount}.`,
                    });
                // enforce fee change permission
                if (!config.canUpdateConvenienceFeePermission) {
                    if (receivedFeeAmounts.ach != undefined && receivedFeeAmounts.ach !== fees.hppAchFeeAmount)
                        return context.createError({
                            message: 'No permission to modify ACH fee',
                        });
                    if (receivedFeeAmounts.eft != undefined && receivedFeeAmounts.eft !== fees.hppEftFeeAmount) {
                        return context.createError({
                            message: 'No permission to modify EFT fee',
                        });
                    }
                    if (receivedFeeAmounts.credit !== undefined &&
                        (receivedFeeAmounts.credit < calculatedMerchantConvenienceFee.amount * (1 - errorMargin) ||
                            receivedFeeAmounts.credit > calculatedMerchantConvenienceFee.amount * (1 + errorMargin)))
                        return context.createError({
                            message: 'No permission to modify credit card fee',
                        });
                }
                else {
                    if (receivedFeeAmounts.ach !== undefined && receivedFeeAmounts.ach !== fees.hppAchFeeAmount)
                        console.info(`ACH fee modified from default ${fees.hppAchFeeAmount} to ${receivedFeeAmounts.ach}`, {
                            default: fees.hppAchFeeAmount,
                            modified: receivedFeeAmounts.ach,
                        });
                    if (receivedFeeAmounts.eft !== undefined && receivedFeeAmounts.eft !== fees.hppEftFeeAmount)
                        console.info(`EFT fee modified from default ${fees.hppEftFeeAmount} to ${receivedFeeAmounts.eft}`, {
                            default: fees.hppEftFeeAmount,
                            modified: receivedFeeAmounts.eft,
                        });
                    if (receivedFeeAmounts.credit !== undefined &&
                        (receivedFeeAmounts.credit < calculatedMerchantConvenienceFee.amount * (1 - errorMargin) ||
                            receivedFeeAmounts.credit > calculatedMerchantConvenienceFee.amount * (1 + errorMargin)))
                        console.info(`Credit fee modified from default ${calculatedMerchantConvenienceFee.amount} to ${receivedFeeAmounts.credit}`, {
                            default: calculatedMerchantConvenienceFee.amount,
                            modified: receivedFeeAmounts.credit,
                        });
                }
                // no issues found
                return true;
            },
        })
            .test({
            name: 'variable',
            message: `Can't use variable fees while also specifying a fixed fee`,
            test: (req, context) => {
                const { merchantId } = context.parent;
                const fees = config.merchantFees[merchantId];
                if (req.ach !== undefined && req.useVariableAch)
                    return false;
                if (req.eft !== undefined && req.useVariableEft)
                    return false;
                if (!config.canUpdateConvenienceFeePermission) {
                    if (req.useVariableAch !== undefined && req.useVariableAch !== fees.hppVariableAchEnabled)
                        return context.createError({
                            message: 'No permission to modify variable ACH fee',
                        });
                    if (req.useVariableEft !== undefined && req.useVariableEft !== fees.hppVariableEftEnabled)
                        return context.createError({
                            message: 'No permission to modify variable EFT fee',
                        });
                }
                return true;
            },
        }),
        allowedPaymentMethods: yup
            .array()
            .of(yup.mixed().oneOf(hppPaymentMethods).defined())
            .unique('allowed payment methods must be unique'),
        countryCode: yup.string().required().length(2),
        locale: yup.string().length(5).required(),
        amount: yup.number().integer().required().min(0),
        referenceId: yup.string().max(36, 'Maxium 36 charachters').required(),
        referenceData: yup.string().optional(),
        paymentDescription: yup.string().optional(),
        returnUrl: yup.string().validUrl('Must provide a valid URL').optional(),
        paymentAttributes: yup
            .array()
            .of(yup.object({
            name: yup
                .mixed()
                .test('is-valid-name', `Name must either be a valid PaymentAttribute or start with "${PUB_SUB_PAYMENT_ATTRIBUTE_PREFIX}"`, (value) => {
                return (typeof value === 'string' &&
                    (value.startsWith(PUB_SUB_PAYMENT_ATTRIBUTE_PREFIX) ||
                        Object.values(PaymentAttribute).includes(value)));
            })
                .required(),
            value: yup.string().required(),
        }))
            .optional(),
        splitPaymentAttributes: yup
            .array()
            .of(yup.array().of(yup.object({
            name: yup.mixed().oneOf(Object.values(PaymentAttribute)).required(),
            value: yup.string().required(),
        })))
            .optional(),
        agentAllowedPremiumFinance: yup.boolean().default(false).notRequired(),
        policies: yup
            .array()
            .of(yup.object(Object.assign(Object.assign({ policyId: yup.string().required() }, (config.isSinglePaymentFlow
            ? {
                minEarnedPremiumPercent: yup.number().required().min(0),
                daysToCancel: yup.number().required().min(0),
                shortRate: yup.boolean().required().default(false),
                auditable: yup.boolean().required().default(false),
                filings: yup.boolean().required().default(false),
                coverageType: yup.string().notRequired().nullable(),
                lineType: yup.string().notRequired().nullable(),
            }
            : {
                minEarnedPremiumPercent: yup.number().notRequired().min(0).nullable(),
                daysToCancel: yup.number().notRequired().min(0).nullable(),
                shortRate: yup.boolean().notRequired().nullable(),
                auditable: yup.boolean().notRequired().nullable(),
                filings: yup.boolean().notRequired().nullable(),
                coverageType: yup.string().notRequired().nullable(),
                lineType: yup.string().notRequired().nullable(),
            })), { mgaInfo: yup
                .object()
                .shape({
                name: yup.string().optional().nullable(),
                address1: yup.string().optional().nullable(),
                address2: yup.string().optional().nullable(),
                city: yup.string().optional().nullable(),
                region: yup.string().optional().nullable(),
                zipOrPostalCode: yup.string().optional().nullable(),
                country: yup.string().optional().nullable(),
            })
                .optional()
                .nullable() })))
            .min(1)
            .default(undefined)
            .notRequired()
            .when(['agentAllowedPremiumFinance', 'pfPaymentOnAccount'], ([agentAllowedPremiumFinance, pfPaymentOnAccount], schema) => config.isSinglePaymentFlow && agentAllowedPremiumFinance && (pfPaymentOnAccount !== null && pfPaymentOnAccount !== void 0 ? pfPaymentOnAccount : null) === null
            ? schema.required()
            : schema),
        brand: yup
            .object()
            .shape({
            name: yup.string().notRequired(),
            phone: yup.string().notRequired(),
            logoUrl: yup.string().notRequired(),
            primaryColor: yup.string().validHex('Must be a valid hexadecimal color value').notRequired(),
            primaryTextColor: yup.string().validHex('Must be a valid hexadecimal color value').notRequired(),
            highlightPrimaryColor: yup.string().validHex('Must be a valid hexadecimal color value').notRequired(),
            highlightPrimaryTextColor: yup.string().validHex('Must be a valid hexadecimal color value').notRequired(),
        })
            .optional(),
        destination: yup.array().test({
            name: 'destination',
            message: 'Destination must be blank, an empty array or any of the following values: ' + APPLIED_PLATFORMS.join(', '),
            test: (value) => {
                if (!(value === null || value === void 0 ? void 0 : value.length))
                    return true;
                return value.every((destination) => APPLIED_PLATFORMS.includes(destination));
            },
        }),
        pfPaymentOnAccount: yup
            .object()
            .shape({
            existingQuoteNumber: yup.string().nullable(),
            client: yup
                .object()
                .shape({
                id: yup.string().required(),
                lookupCode: yup.string().required(),
                name: yup.string(),
                phone: yup.string(),
                email: yup.string(),
                address: yup
                    .object()
                    .shape({
                    address1: yup.string(),
                    address2: yup.string(),
                    city: yup.string(),
                    region: yup.string(),
                    zipOrPostalCode: yup.string(),
                    country: yup.string(),
                })
                    .default(undefined)
                    .notRequired(),
            })
                .required(),
            policies: yup
                .array()
                .of(yup.object().shape({
                policyId: yup.string(),
                policyNumber: yup.string(),
                paymentType: yup.string().required(),
                estimatedPremium: yup.number().required(),
                estimatedCommission: yup.number().required(),
                fullyEarnedFees: yup.number().required(),
                financedFees: yup.number().required(),
                financedTaxes: yup.number().required(),
                effectiveDate: yup.string().required(),
                expirationDate: yup.string().required(),
                minEarnedPercent: yup.number().min(0).max(100).required(),
                isAuditable: yup.boolean().default(false),
                isShortRate: yup.boolean().default(false),
                isFilings: yup.boolean().default(false),
                daysToCancel: yup.number().min(0).required(),
                branch: yup
                    .object()
                    .shape({
                    name: yup.string(),
                    email: yup.string(),
                    address: yup
                        .object()
                        .shape({
                        address1: yup.string(),
                        address2: yup.string(),
                        city: yup.string(),
                        region: yup.string(),
                        zipOrPostalCode: yup.string(),
                        country: yup.string(),
                    })
                        .optional()
                        .nullable(),
                })
                    .required(),
                structure: yup
                    .object()
                    .shape({
                    agencyId: yup.string().required(),
                    branchId: yup.string().required(),
                })
                    .required(),
                lines: yup
                    .array()
                    .of(yup.object().shape({
                    id: yup.string(),
                    lineTypeCode: yup.string().required(),
                    businessType: yup.string().required(),
                    isCustomForm: yup.boolean().required(),
                    description: yup.string(),
                }))
                    .min(1)
                    .required(),
                issuingCompany: yup
                    .object({
                    id: yup.string().required(),
                    lookupCode: yup.string().required(),
                    name: yup.string(),
                    phone: yup.string(),
                    address: yup
                        .object({
                        address1: yup.string(),
                        address2: yup.string(),
                        city: yup.string(),
                        region: yup.string(),
                        zipOrPostalCode: yup.string(),
                        country: yup.string(),
                    })
                        .default(undefined)
                        .notRequired(),
                })
                    .default(undefined)
                    .notRequired(),
                premiumPayableEntity: yup
                    .object({
                    id: yup.string().required(),
                    lookupCode: yup.string().required(),
                    name: yup.string(),
                    phone: yup.string(),
                    address: yup
                        .object({
                        address1: yup.string(),
                        address2: yup.string(),
                        city: yup.string(),
                        region: yup.string(),
                        zipOrPostalCode: yup.string(),
                        country: yup.string(),
                    })
                        .default(undefined)
                        .notRequired(),
                })
                    .default(undefined)
                    .notRequired(),
            }))
                .min(1)
                .required(),
        })
            .default(undefined)
            .notRequired(),
    })
        .required()
        .test('is-single-pf-flow', 'Cannot accept both PF Payment on Policy and PF Payment on Account payloads', (value) => {
        var _a, _b;
        return !(value.agentAllowedPremiumFinance === true &&
            ((_a = value.pfPaymentOnAccount) !== null && _a !== void 0 ? _a : null) !== null &&
            ((_b = value.policies) !== null && _b !== void 0 ? _b : null) !== null);
    });
};
export const makeBulkPaymentSessionsSchema = ({ merchantFees, canUpdateConvenienceFeePermission, }) => {
    const isSinglePaymentFlow = false;
    const schema = paymentSessionSchema({
        merchantFees,
        canUpdateConvenienceFeePermission,
        isSinglePaymentFlow,
    });
    return yup.array().of(schema);
};
export const paymentSessionUpdateSchema = yup
    .object({
    pspSessionId: yup.string().required(),
    customer: yup
        .object()
        .shape({
        businessName: yup.string().optional(),
        firstName: yup.string().optional(),
        lastName: yup.string().optional(),
        email: yup.string().email().optional(),
    })
        .optional(),
    countryCode: yup.string().optional().length(2),
    checkoutBEnabled: yup.boolean().optional().default(false),
})
    .required();
export const hostedPaymentSessionSchema = yup
    .object({
    customer: yup
        .object()
        .shape({
        id: yup.string().optional(),
        firstName: yup.string().optional(),
        lastName: yup.string().optional(),
        businessName: yup.string().optional(),
        email: yup.string().email().optional(),
    })
        .optional(),
    locale: yup.string().length(5).required(),
    amount: yup.number().integer().required().min(0),
    paymentDescription: yup.string().optional(),
    paymentAttributes: yup
        .array()
        .of(yup
        .object({
        name: yup
            .mixed()
            .test('is-valid-name', `Name must either be a valid PaymentAttribute or start with "${PUB_SUB_PAYMENT_ATTRIBUTE_PREFIX}"`, (value) => {
            return (typeof value === 'string' &&
                (value.startsWith(PUB_SUB_PAYMENT_ATTRIBUTE_PREFIX) ||
                    Object.values(PaymentAttribute).includes(value)));
        })
            .required(),
        value: yup.string().required(),
    })
        .required())
        .optional(),
    splitPaymentAttributes: yup
        .array()
        .of(yup
        .array()
        .of(yup
        .object({
        name: yup.mixed().oneOf(Object.values(PaymentAttribute)).required(),
        value: yup.string().required(),
    })
        .required())
        .required())
        .optional(),
    epicInvoiceMeta: yup
        .object({
        clientId: yup.string().required(),
        transactions: yup
            .array()
            .of(yup
            .object({
            id: yup.string().required(),
            amount: yup
                .object({
                units: yup.number().required(),
                partialUnits: yup.number().required(),
            })
                .required(),
            classCode: yup.string().required(),
            itemNumber: yup.string().required(),
            invoiceNumber: yup.string().required(),
        })
            .required()
            .default(undefined))
            .optional(),
    })
        .optional()
        .default(undefined),
    workflow: yup.number().required(),
    pfQuickQuoteId: yup.string().optional(),
    checkoutBEnabled: yup.boolean().optional().default(false),
})
    .required();
export var Workflow;
(function (Workflow) {
    Workflow[Workflow["Default"] = 1] = "Default";
    Workflow[Workflow["SingleAmount"] = 2] = "SingleAmount";
    Workflow[Workflow["SingleAmountWithRateLimitBypass"] = 2.5] = "SingleAmountWithRateLimitBypass";
    Workflow[Workflow["SingleAmountWithOptionalBypass"] = 2.75] = "SingleAmountWithOptionalBypass";
    Workflow[Workflow["SingleInvoice"] = 3] = "SingleInvoice";
    Workflow[Workflow["MultiInvoice"] = 4] = "MultiInvoice";
    Workflow[Workflow["AppliedOnAccount"] = 5.2] = "AppliedOnAccount";
    Workflow[Workflow["AppliedToInvoice"] = 5.4] = "AppliedToInvoice";
    Workflow[Workflow["AutopayMultiple"] = 6] = "AutopayMultiple";
    Workflow[Workflow["AutopayAccount"] = 6.1] = "AutopayAccount";
    Workflow[Workflow["AutopayPolicies"] = 6.2] = "AutopayPolicies";
    Workflow[Workflow["AutopayInvoices"] = 6.3] = "AutopayInvoices";
})(Workflow || (Workflow = {}));
//# sourceMappingURL=paymentSession.js.map